import { PartialNullable } from "../../Extensions";
import { PropertyChange } from "../../PropertyChange";
import { Property, PropertyString } from "../../RadObject";
import { ClientAwareCategoryAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareCategoryAttribute";
import { ClientAwareDisplayNameAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareDisplayNameAttribute";
import { ElementRef } from "../ElementRef";
import { JsxFragment } from "../JsxFragment";
import { ContainerControl, ContainerControlPropertyValues, IContainerControl } from "./ContainerControl";
import { NAMESPACE } from "./_Namespace";

import "./GroupBox.scss";

export type GroupBoxPropertyValues =
    ContainerControlPropertyValues
    & {
        headerAlignment: string;
    };

export type GroupBoxProperties = {
    [name in keyof GroupBoxPropertyValues]: Property<GroupBoxPropertyValues[name]>
};

export interface IGroupBox extends IContainerControl, GroupBoxProperties
{
}

export class GroupBox extends ContainerControl implements IGroupBox
{
    // #region Static

    public static override classNamespace = NAMESPACE + "GroupBox";
    public static override className = "GroupBox";

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<GroupBoxPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                headerAlignment: "left",
                text: "GroupBox",
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    protected _headerElementRef!: ElementRef;
    protected _titleElementRef!: ElementRef;

    // #endregion

    // #region Properties

    public headerAlignment!: PropertyString;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<GroupBoxPropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-group-box ui segments");
        this._headerElementRef.current.addClass("ui segment secondary");
        this._containerElementRef.current.addClass("ui segment");

        this._headerElementRef.current.css({ textAlign: this.headerAlignment.get() });
    }

    // #endregion

    // #region Protected Methods

    protected _headerAlignmentChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: GroupBoxPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this._headerElementRef = new ElementRef();
        this._titleElementRef = new ElementRef();

        this.headerAlignment = new PropertyString({
            context: this,
            initialValue: propertyValues.headerAlignment,
            changeHandler: this._headerAlignmentChanged,
            attributes: [
                new ClientAwareCategoryAttribute({ category: "Appearance" }),
                new ClientAwareDisplayNameAttribute({ displayName: "Header Alignment" }),
            ],
        });
    }

    protected override _createElementHtml(children?: JsxFragment | string | (JsxFragment | string | undefined)[])
    {
        return (
            <div>
                <div ref={this._headerElementRef} class="rad-header">
                    <h5 ref={this._titleElementRef}></h5>
                </div>
                <div ref={this._containerElementRef} class="rad-control-container">
                    {children ?? this._createChildrenHtml()}
                </div>
            </div>
        );
    }
    protected override _textChanged(change: PropertyChange<string>)
    {
        super._textChanged(change);
        this._titleElementRef.current.text(change.currentValue);
    }

    // #endregion

    // #endregion
}